import Phaser from 'phaser'
import type { Projectile } from '@interfaces/index'

// Element colours matching TowerRenderer / UnitRenderer palettes
const ELEMENT_COLORS: Record<string, number> = {
  fire:    0xe8613a,
  water:   0x3a8ae8,
  nature:  0x4aaa3a,
  elektro: 0xe8d030,
  light:   0xf0eeff,
  shadow:  0x8a50d0,
  neutral: 0xffffff,
}

export class ProjectileRenderer {
  private readonly scene: Phaser.Scene
  private readonly graphics: Phaser.GameObjects.Graphics

  constructor(scene: Phaser.Scene) {
    this.scene = scene
    this.graphics = scene.add.graphics()
    // Render above units (depth 10) but below UI
    this.graphics.setDepth(15)
  }

  render(projectiles: Projectile[]): void {
    this.graphics.clear()

    for (const p of projectiles) {
      if (p.hit) continue
      const color = ELEMENT_COLORS[p.el] ?? 0xffffff
      this._drawByType(p, color)
    }
  }

  private _drawByType(p: Projectile, color: number): void {
    const { curX: x, curY: y } = p

    switch (p.towerTypeId) {
      case 'archer':
        this._drawArrow(x, y, color, p)
        break
      case 'firemage':
        this._drawFireball(x, y, color)
        break
      case 'frost':
        this._drawCrystal(x, y, color)
        break
      case 'lightning':
        this._drawLightningOrb(x, y, color)
        break
      case 'shadow':
        this._drawShadowOrb(x, y, color)
        break
      default:
        this._drawDefault(x, y, color)
    }
  }

  // Archer: thin elongated oval pointing toward target direction
  private _drawArrow(x: number, y: number, color: number, p: Projectile): void {
    const dx = p.toX - p.fromX
    const dy = p.toY - p.fromY
    const len = Math.sqrt(dx * dx + dy * dy) || 1
    const nx = dx / len
    const ny = dy / len

    // Arrow shaft: thin rectangle along travel direction (6px long, 2px wide)
    const halfLen = 5
    const halfW = 1.5
    const px = -ny
    const py = nx

    this.graphics.fillStyle(color, 0.9)
    this.graphics.beginPath()
    this.graphics.moveTo(x + nx * halfLen + px * halfW, y + ny * halfLen + py * halfW)
    this.graphics.lineTo(x + nx * halfLen - px * halfW, y + ny * halfLen - py * halfW)
    this.graphics.lineTo(x - nx * halfLen - px * halfW, y - ny * halfLen - py * halfW)
    this.graphics.lineTo(x - nx * halfLen + px * halfW, y - ny * halfLen + py * halfW)
    this.graphics.closePath()
    this.graphics.fillPath()

    // Arrowhead
    this.graphics.fillStyle(0xffffff, 0.8)
    this.graphics.fillTriangle(
      x + nx * (halfLen + 3), y + ny * (halfLen + 3),
      x + nx * halfLen + px * 3, y + ny * halfLen + py * 3,
      x + nx * halfLen - px * 3, y + ny * halfLen - py * 3
    )
  }

  // Firemage: large orange-red fireball with fiery glow
  private _drawFireball(x: number, y: number, color: number): void {
    // Outer glow
    this.graphics.fillStyle(color, 0.15)
    this.graphics.fillCircle(x, y, 11)
    // Mid glow
    this.graphics.fillStyle(color, 0.35)
    this.graphics.fillCircle(x, y, 8)
    // Core
    this.graphics.fillStyle(color, 1.0)
    this.graphics.fillCircle(x, y, 5)
    // Bright white-yellow hot center
    this.graphics.fillStyle(0xffee88, 0.9)
    this.graphics.fillCircle(x - 1, y - 1, 2.5)
  }

  // Frost: ice crystal — a 6-point snowflake / diamond
  private _drawCrystal(x: number, y: number, color: number): void {
    // Glow
    this.graphics.fillStyle(color, 0.25)
    this.graphics.fillCircle(x, y, 8)
    // Diamond core (4 vertices)
    const r = 5
    this.graphics.fillStyle(color, 0.9)
    this.graphics.beginPath()
    this.graphics.moveTo(x, y - r)
    this.graphics.lineTo(x + r * 0.6, y)
    this.graphics.lineTo(x, y + r)
    this.graphics.lineTo(x - r * 0.6, y)
    this.graphics.closePath()
    this.graphics.fillPath()
    // White specular facet
    this.graphics.fillStyle(0xeeffff, 0.85)
    this.graphics.fillTriangle(x, y - r, x + r * 0.6, y, x - r * 0.6, y)
  }

  // Lightning: bright yellow-white electric orb with cross sparks
  private _drawLightningOrb(x: number, y: number, color: number): void {
    // Glow ring
    this.graphics.fillStyle(color, 0.2)
    this.graphics.fillCircle(x, y, 9)
    // Core orb
    this.graphics.fillStyle(color, 1.0)
    this.graphics.fillCircle(x, y, 4)
    // Cross sparks (4 arms)
    const spLen = 7
    this.graphics.lineStyle(1.5, 0xffffff, 0.9)
    this.graphics.beginPath()
    this.graphics.moveTo(x - spLen, y); this.graphics.lineTo(x + spLen, y)
    this.graphics.moveTo(x, y - spLen); this.graphics.lineTo(x, y + spLen)
    this.graphics.strokePath()
    // Diagonal mini sparks
    this.graphics.lineStyle(1, 0xffffff, 0.5)
    this.graphics.beginPath()
    this.graphics.moveTo(x - 4, y - 4); this.graphics.lineTo(x + 4, y + 4)
    this.graphics.moveTo(x + 4, y - 4); this.graphics.lineTo(x - 4, y + 4)
    this.graphics.strokePath()
  }

  // Shadow: dark orb with inverted glow (dark aura, bright ring)
  private _drawShadowOrb(x: number, y: number, color: number): void {
    // Outer bright ring
    this.graphics.lineStyle(1.5, color, 0.7)
    this.graphics.strokeCircle(x, y, 8)
    // Dark inner field
    this.graphics.fillStyle(0x110022, 0.85)
    this.graphics.fillCircle(x, y, 6)
    // Colored core
    this.graphics.fillStyle(color, 0.9)
    this.graphics.fillCircle(x, y, 3)
    // Specular
    this.graphics.fillStyle(0xddaaff, 0.5)
    this.graphics.fillCircle(x - 1, y - 1, 1.2)
  }

  // Default (goldmine, wall, etc. — should not appear since projSpeed=0)
  private _drawDefault(x: number, y: number, color: number): void {
    this.graphics.fillStyle(color, 0.3)
    this.graphics.fillCircle(x, y, 7)
    this.graphics.fillStyle(color, 1.0)
    this.graphics.fillCircle(x, y, 4)
    this.graphics.fillStyle(0xffffff, 0.6)
    this.graphics.fillCircle(x - 1, y - 1, 1.5)
  }

  destroy(): void {
    this.graphics.destroy()
  }

  getScene(): Phaser.Scene {
    return this.scene
  }
}
